// Sorunlu kargo motoru — SAF kurallar (DB/ağ yok). Webpostman gönderi kaydını değerlendirip "müdahale
// gerektiriyor mu, hangi aşamada, neden" sorusunu yanıtlar ve operatör görev kuyruğunu üretir.
//
// Kaynak: erpanel yöneticisinin "Kargo Kontrol" paneli (kural setinin ilk hali; bkz. memory
// opop-erpanel-kargo-kontrol-panel) OPOP'a uyarlandı. Farklar:
//  - OPOP'un mevcut "Sorunlu Kargo" kuralları (festToOrderStatusName) de aktif sayılır; operatörün
//    sipariş listesinde gördüğü sorunlu ile kuyruktaki sorunlu tutarlı kalır.
//  - Kanal eşikleri cariye özgü koddan değil çözülmüş kanaldan (ARAS/PTT…) okunur.
//  - Ödeme tipine göre risk, Webpostman `odeme_turu`ndan DEĞİL müşterinin OPOP'taki ödeme yönteminden
//    hesaplanır: Webpostman teslimde ödeme türünü fiili tahsilata çevirdiği için (KV→PÖ) o alana göre
//    teslim oranı döngüseldir (bkz. memory opop-cargo-amount-type-default).
import { CARGO_ORDER_STATUS, festToOrderStatusName, isAtBranchPickupWait } from './types'

// ─────────────────────────────── Neden sınıflandırması ───────────────────────────────

export type ProblemReason =
  | 'ulasilamiyor'
  | 'adres'
  | 'bolge'
  | 'bekleme'
  | 'ucret_ret'
  | 'tatil'
  | 'takili'
  | 'diger'

export interface ProblemReasonInfo {
  label: string
  short: string
  /** Operatöre ne yapması gerektiği. */
  action: string
}

export const PROBLEM_REASONS: Record<ProblemReason, ProblemReasonInfo> = {
  ulasilamiyor: {
    label: 'Ulaşılamıyor / not bırakıldı',
    short: 'Ulaşılamıyor',
    action: "Müşteriyi ara. Telefon kapalıysa WhatsApp'tan yaz; kurye tekrar çıkmadan önce müsait olduğu saati öğren.",
  },
  adres: {
    label: 'Adres hatalı / tanınmıyor',
    short: 'Adres',
    action: 'Açık adresi müşteriden al, adres düzeltmesini kargo firmasına ilet. Mahalle, sokak, bina no ve tarif iste.',
  },
  bolge: {
    label: 'Bölge dışı / mobil gün / köy',
    short: 'Bölge dışı',
    action: 'Adres dağıtım bölgesi dışında. İlçe merkezinde şubeden teslim almayı ya da yakın bir adresi öner.',
  },
  bekleme: {
    label: 'Şubede bekliyor / süre doluyor',
    short: 'Şubede bekliyor',
    action: 'Bekleme süresi bitmeden ara; iade olmadan teslim aldır. Şube telefonunu müşteriye ilet.',
  },
  ucret_ret: {
    label: 'Kabul etmedi / ücret itirazı',
    short: 'Kabul etmedi',
    action: 'İtiraz sebebini öğren (fiyat, vazgeçme, yanlış sipariş). İkna edilemezse iadeyi başlat, tekrar dağıtıma sokma.',
  },
  tatil: {
    label: 'Tatil / hafta sonu / ertelendi',
    short: 'Tatil-erteleme',
    action: 'Genelde kendiliğinden tekrar dağıtıma çıkar. Müşteriye bilgi ver, telefonu açık tutmasını söyle.',
  },
  takili: {
    label: 'Hareketsiz / dağıtımda takıldı',
    short: 'Takıldı',
    action: 'Normal teslim süresini aştı, hareket yok. Şubeyi arayıp gönderinin yerini sor; gerekirse kayıp/soruşturma talebi aç.',
  },
  diger: {
    label: 'Diğer',
    short: 'Diğer',
    action: 'Hareketlere bak, gerekirse şubeyi ara.',
  },
}

/** Türkçe katlama: tr küçük harf + ı/ş/ğ/ü/ö/ç → ascii (birleşik nokta da temizlenir). */
function foldTr(s: string): string {
  return s
    .toLocaleLowerCase('tr-TR')
    .replace(/̇/g, '')
    .replace(/ı/g, 'i').replace(/ş/g, 's').replace(/ğ/g, 'g')
    .replace(/ü/g, 'u').replace(/ö/g, 'o').replace(/ç/g, 'c')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Kargo sonuç metninden (Webpostman `sonuc`, 20 karakterde kesik gelebilir) neden çıkarır.
 * Sıra önemlidir: "adres bulunamadı" önce adres sayılır, sonra genel "bulunamadı" ulaşılamıyor.
 */
export function reasonOf(sonuc: string | null | undefined, opts: { stale?: boolean } = {}): ProblemReason {
  const s = foldTr(sonuc ?? '')
  if (/adres hatali|taninmiyor|tanimiyor|adres bulunamadi|adres yetersiz|eksik adres/.test(s)) return 'adres'
  if (/bolge disi|mobil|koy dagitim|at yetismedi|kapsam disi/.test(s)) return 'bolge'
  if (/bekleme suresi|subeden alacak|isyerinde bekl|subede bekl/.test(s)) return 'bekleme'
  if (/tasima ucret|kabul edilmedi|reddetti|vazgec|iptal/.test(s)) return 'ucret_ret'
  if (/tatil|hafta ?sonu|resmi/.test(s)) return 'tatil'
  if (/not birak|alici yok|ulasilamad|telefon|kapali|evde yok|adreste yok|bulunamad/.test(s)) return 'ulasilamiyor'
  if (opts.stale) return 'takili'
  return 'diger'
}

// ─────────────────────────────── Aşama + aktiflik ───────────────────────────────

/** Müdahale listesindeki aşama. */
export type ProblemStage =
  | 'haber' // kurye adrese gitti, haber formu bıraktı
  | 'kayip'
  | 'sorunlu' // teslim edilemedi / OPOP sorunlu kuralları (bölge, PTT işyerinde bekliyor…)
  | 'tekrar' // tekrar dağıtım planlandı
  | 'takili' // normal teslim süresini aştı, hareket yok

export const PROBLEM_STAGE_LABELS: Record<ProblemStage, string> = {
  haber: 'Haber formu',
  kayip: 'Kayıp',
  sorunlu: 'Teslim edilemedi',
  tekrar: 'Tekrar dağıtım',
  takili: 'Takılı kalan',
}

export type ProblemOutcome = 'delivered' | 'returned'

export interface ProblemSettings {
  /** Kanal → normal teslim süresi (gün). Bu kadar gün hareketsiz kalan açık gönderi "takılı" sayılır. */
  normalDaysByChannel: Record<string, number>
  defaultNormalDays: number
}

export const DEFAULT_PROBLEM_SETTINGS: ProblemSettings = {
  // Aras ~3 gün, PTT ağı ~7 gün (panelde ölçülen normal teslim süreleri).
  normalDaysByChannel: { PTT: 7 },
  defaultNormalDays: 3,
}

export function normalDeliveryDays(channel: string | null | undefined, settings: ProblemSettings = DEFAULT_PROBLEM_SETTINGS): number {
  return (channel && settings.normalDaysByChannel[channel]) || settings.defaultNormalDays
}

/** Değerlendirme girdisi (cargo_shipments satırının ilgili alanları). */
export interface ProblemInput {
  /** Durum kovası 0-6. */
  durum: number | null
  /** İki haneli statü kodu. */
  statuNo: string | null
  sonuc: string | null
  /** Son hareket zamanı (ISO). */
  degisimAt: string | null
  /** Çözülmüş kanal (ARAS/PTT…). */
  channel: string | null
  /** Sipariş şube teslim: şubede alıcıyı bekleyen gönderi sorunlu sayılmaz ("Şube teslim" sekmesinde izlenir). */
  branchDelivery?: boolean
}

export interface ProblemEvaluation {
  /** Müdahale gerektiriyor mu (kuyrukta mı). */
  active: boolean
  stage: ProblemStage | null
  reason: ProblemReason | null
  /** Nihai sonuç (teslim / iade); hâlâ açıksa null. */
  outcome: ProblemOutcome | null
}

/** İade statüleri: 20 başladı, 21 göndericiye ulaştı, 22-24 iade yolunda. */
export const RETURN_STATUS_CODES = ['20', '21', '22', '23', '24']
const DAY_MS = 86_400_000

/**
 * Gönderi iade sürecinde ya da iade edilmiş mi (kova 2 ya da iade statüsü)? Bu gönderiler artık aranmaz,
 * müdahale edilmez; kuyruklardan (müdahale, görev, söz, erken müdahale) çıkar (kullanıcı kararı 2026-10-04).
 */
export function isReturningShipment(s: { durum: number | null; statuNo: string | null }): boolean {
  return s.durum === 2 || RETURN_STATUS_CODES.includes(s.statuNo ?? '')
}

/** Kabul statüleri: 00 Kabul Bekliyor, 01 Kabul Edildi — veri kargo firmasında, fiziksel hareket yok. */
export const NOT_DEPARTED_STATUS_CODES = ['00', '01']

/**
 * Gönderi hiç yola çıkmamış mı (kova 0 ya da kabul statüsü)? Veri kargo firmasına iletilmiş ama paket
 * teslim edilmemiş/paketlemede kalmış; müşteriyi aramanın anlamı yok → kuyruklara girmez (kullanıcı kararı
 * 2026-10-05). Kurye paketi alınca kova/statü değişir ve gönderi normal akışa döner.
 */
export function isNotDepartedShipment(s: { durum: number | null; statuNo: string | null }): boolean {
  return s.durum === 0 || NOT_DEPARTED_STATUS_CODES.includes(s.statuNo ?? '')
}

/**
 * Gönderiyi değerlendirir. Kurallar (panelden uyarlandı):
 *  - Teslim (kova 1 / statü 10) nihaidir.
 *  - İade sürecine giren (kova 2 / statü 20-24) gönderi aranmaz → doğrudan iade ("returned").
 *  - Hiç yola çıkmamış (kova 0 / statü 00-01) gönderi aranmaz → aktif değil, sonuç yok.
 *  - Haber formu (3), kayıp (5), sorunlu (6), tekrar dağıtım (30), teslim edilemedi (50/60) → aktif.
 *  - OPOP'un "Sorunlu Kargo" kuralları (PTT işyerinde bekliyor, köy dağıtımı, şubeden alacak…) → aktif.
 *  - Şube teslim siparişinde şubede alıcıyı bekleyen gönderi (kayıp hariç) aktif değil.
 *  - Açık gönderi (kova 4) kanalın normal teslim süresinden uzun hareketsizse → aktif ("takılı").
 */
export function evaluateShipment(
  s: ProblemInput,
  now: Date = new Date(),
  settings: ProblemSettings = DEFAULT_PROBLEM_SETTINGS,
): ProblemEvaluation {
  const statu = s.statuNo ?? ''
  const durum = s.durum
  const inactive = (outcome: ProblemOutcome | null): ProblemEvaluation => ({ active: false, stage: null, reason: null, outcome })

  if (durum === 1 || statu === '10') return inactive('delivered')
  if (isReturningShipment({ durum, statuNo: statu })) return inactive('returned')
  if (isNotDepartedShipment({ durum, statuNo: statu })) return inactive(null)
  if (s.branchDelivery && durum !== 5 && isAtBranchPickupWait(s.sonuc)) return inactive(null)

  const ageMs = s.degisimAt ? now.getTime() - new Date(s.degisimAt).getTime() : null

  const opopProblem =
    festToOrderStatusName(statu, durum != null ? String(durum) : undefined, s.sonuc ?? undefined) === CARGO_ORDER_STATUS.problem
  const open = durum == null || durum === 4
  const stale = open && ageMs != null && ageMs > normalDeliveryDays(s.channel, settings) * DAY_MS

  let stage: ProblemStage | null = null
  if (durum === 3) stage = 'haber'
  else if (durum === 5) stage = 'kayip'
  else if (durum === 6 || statu === '50' || statu === '60') stage = 'sorunlu'
  else if (statu === '30') stage = 'tekrar'
  else if (opopProblem) stage = 'sorunlu'
  else if (stale) stage = 'takili'

  if (!stage) return inactive(null)
  return { active: true, stage, reason: reasonOf(s.sonuc, { stale: stage === 'takili' }), outcome: null }
}

// ─────────────────────────────── Öncelik ───────────────────────────────

export interface PriorityInput {
  stage: ProblemStage
  reason: ProblemReason
  /** Kabulden bu yana geçen gün. */
  waitDays: number
  amount: number
  callbackDue: boolean
  callbackPending: boolean
  handledToday: boolean
}

/**
 * Müdahale listesi sırası: bekleme uzadıkça ve tutar arttıkça yükselir; haber formu ve şubede bekleyen
 * öne çıkar; geri arama zamanı gelen en üste, bugün işlenen ve geri araması ileri tarihli olan aşağı iner.
 */
export function problemPriority(p: PriorityInput): number {
  const score =
    p.waitDays * 3 +
    p.amount / 50 +
    (p.stage === 'haber' ? 12 : 0) +
    (p.reason === 'bekleme' ? 15 : 0) +
    (p.callbackDue ? 60 : 0) -
    (p.handledToday && !p.callbackDue ? 40 : 0) -
    (p.callbackPending ? 30 : 0)
  return Math.round(score)
}

// ─────────────────────────────── Görüşme sonuçları ───────────────────────────────

export type ContactOutcomeGroup = 'ulasildi' | 'reddetti' | 'ulasilamadi' | 'kargo' | 'diger'

export const CONTACT_OUTCOME_GROUP_LABELS: Record<ContactOutcomeGroup, string> = {
  ulasildi: 'Ulaşıldı',
  reddetti: 'Teslim almayacak',
  ulasilamadi: 'Ulaşılamadı',
  kargo: 'Kargo firması',
  diger: 'Diğer',
}

/**
 * Görüşme sonuçları. İlk 9 kod eski "görüşme neticesi" seçenekleriyle AYNI (geçmiş kayıtlar ve operatör
 * alışkanlığı korunur); panelden gelenler eklendi (ulaşılamadı, telefon kapalı, sonra aranacak, mesaj, adres).
 *  - contact: müşteriyle temas sayılır (günlük/toplam limitlere girer).
 *  - positive: olumlu görüşme → gönderi teslim olmazsa doğrulama araması.
 *  - unreached: ulaşılamadı → birkaç saat sonra tekrar aranır.
 *  - final: müşteri teslim almayacak → artık arama görevi üretilmez (iade kararı).
 *  - date: söz/dağıtım günü girilebilir (söz takibine girer).
 */
export const CONTACT_OUTCOMES = [
  { key: 'teslim_alacak', label: 'Teslim alacak', group: 'ulasildi', contact: true, positive: true, date: true },
  { key: 'dagitim_gunu_bekliyor', label: 'Dağıtım günü bekliyor', group: 'ulasildi', contact: true, positive: true, date: true },
  { key: 'ulasildi_adres', label: 'Adres düzeltildi', group: 'ulasildi', contact: true, positive: true },
  { key: 'teslim_almis', label: 'Teslim almış', group: 'ulasildi', contact: true, positive: true },
  { key: 'vazgecti_teslim_almayacak', label: 'Vazgeçmiş — teslim almayacak', group: 'reddetti', contact: true, final: true },
  { key: 'kusurlu_teslim_almayacak', label: 'Kusurlu/hatalı kargo — teslim almayacak', group: 'reddetti', contact: true, final: true },
  { key: 'siparis_vermemis_teslim_almayacak', label: 'Sipariş vermemiş — teslim almayacak', group: 'reddetti', contact: true, final: true },
  { key: 'ulasilamadi', label: 'Ulaşılamadı (açmadı)', group: 'ulasilamadi', contact: true, unreached: true },
  { key: 'kapali', label: 'Telefon kapalı / yanlış numara', group: 'ulasilamadi', contact: true, unreached: true },
  { key: 'tekrar_ara', label: 'Müsait değil — sonra aranacak', group: 'ulasilamadi', contact: true, unreached: true },
  { key: 'mesaj_gonderildi', label: 'WhatsApp mesajı gönderildi', group: 'ulasilamadi', contact: true, unreached: true },
  { key: 'kargo_gorusuldu', label: 'Kargo/şube ile görüşüldü', group: 'kargo', contact: false, positive: true },
  { key: 'kargoya_ulasilamadi', label: 'Kargoya ulaşılamadı', group: 'kargo', contact: false },
  { key: 'kargo_talep', label: 'Kargo firmasına talep iletildi', group: 'kargo', contact: false, positive: true },
  { key: 'diger', label: 'Diğer', group: 'diger', contact: true, noteRequired: true },
  { key: 'not', label: 'Not', group: 'diger', contact: false, noteRequired: true },
] as const satisfies readonly {
  key: string
  label: string
  group: ContactOutcomeGroup
  contact: boolean
  positive?: boolean
  unreached?: boolean
  final?: boolean
  date?: boolean
  noteRequired?: boolean
}[]

export type ContactOutcome = (typeof CONTACT_OUTCOMES)[number]['key']
type OutcomeDef = (typeof CONTACT_OUTCOMES)[number]

const outcomeKeys = (pred: (o: OutcomeDef) => boolean) => CONTACT_OUTCOMES.filter(pred).map((o) => o.key) as ContactOutcome[]
const flag = (o: OutcomeDef, k: 'positive' | 'unreached' | 'final' | 'date' | 'noteRequired') => (o as Record<string, unknown>)[k] === true

/** Müşteriyle temas sayılan sonuçlar (limitlere girer). */
export const CONTACT_OUTCOME_KEYS = outcomeKeys((o) => o.contact)
/** Ulaşılamayan / yanıtsız kalan temaslar → tekrar aranır. */
export const UNREACHED_OUTCOMES = outcomeKeys((o) => flag(o, 'unreached'))
/** Olumlu sonuçlar → gönderi hâlâ teslim olmadıysa doğrulama araması. */
export const POSITIVE_OUTCOMES = outcomeKeys((o) => flag(o, 'positive'))
/** Müşteri teslim almayacak → arama görevi üretilmez. */
export const FINAL_OUTCOMES = outcomeKeys((o) => flag(o, 'final'))
/** Söz/dağıtım günü girilebilen sonuçlar. */
export const DATED_OUTCOMES = outcomeKeys((o) => flag(o, 'date'))

export function isContactOutcome(key: string): key is ContactOutcome {
  return CONTACT_OUTCOMES.some((o) => o.key === key)
}

export function contactOutcomeLabel(key: string | null | undefined): string {
  if (!key) return '—'
  return CONTACT_OUTCOMES.find((o) => o.key === key)?.label ?? key
}

export interface ContactInput {
  outcome: string
  note?: string | null
  /** Söz/dağıtım günü (YYYY-MM-DD). */
  promisedDate?: string | null
  /** Planlanan geri arama (ISO). */
  callbackAt?: string | null
}

export type ValidatedContact = { outcome: ContactOutcome; note: string | null; promisedDate: string | null; callbackAt: string | null }

/**
 * Görüşme girdisini doğrular/normalize eder. Not gerektiren sonuçlarda (Diğer, Not) not zorunlu;
 * tarih yalnız söz/dağıtım sonuçlarında kabul edilir ve bugünden en fazla 30 gün sonrası olabilir;
 * geri arama gelecekte ve en fazla 14 gün içinde olmalı.
 */
export function validateContactInput(input: ContactInput, now: Date = new Date()): { ok: true; value: ValidatedContact } | { ok: false; error: string } {
  if (!isContactOutcome(input.outcome)) return { ok: false, error: 'Geçersiz görüşme sonucu.' }
  const def = CONTACT_OUTCOMES.find((o) => o.key === input.outcome)!
  const note = input.note?.trim() ? input.note.trim().slice(0, 2000) : null
  if (flag(def, 'noteRequired') && !note) return { ok: false, error: `"${def.label}" için açıklama notu zorunludur.` }

  let promisedDate: string | null = null
  if (input.promisedDate) {
    if (!flag(def, 'date')) return { ok: false, error: 'Tarih yalnız teslim alma / dağıtım günü sonuçlarında girilebilir.' }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(input.promisedDate)) return { ok: false, error: 'Geçersiz tarih.' }
    const diff = diffCalendarDays(input.promisedDate, istanbulClock(now).date)
    if (diff < 0 || diff > 30) return { ok: false, error: 'Söz günü bugünden itibaren en fazla 30 gün içinde olmalı.' }
    promisedDate = input.promisedDate
  }

  let callbackAt: string | null = null
  if (input.callbackAt) {
    const t = Date.parse(input.callbackAt)
    if (!Number.isFinite(t)) return { ok: false, error: 'Geçersiz geri arama zamanı.' }
    if (t < now.getTime() - 60_000 || t > now.getTime() + 14 * DAY_MS) {
      return { ok: false, error: 'Geri arama zamanı gelecekte ve en fazla 14 gün içinde olmalı.' }
    }
    callbackAt = new Date(t).toISOString()
  }
  return { ok: true, value: { outcome: def.key, note, promisedDate, callbackAt } }
}

/**
 * Müşterinin OPOP'ta seçtiği ödeme → niyet. Online/havale önceden ödenmiştir; adında kart/kredi geçen
 * kapıda kart, nakit geçen kapıda nakittir. (Webpostman ödeme türü teslimde değiştiği için KULLANILMAZ.)
 */
export function paymentIntentOf(paymentName: string | null | undefined, onlinePaid = false): PaymentIntent {
  if (onlinePaid) return 'prepaid'
  const f = foldTr(paymentName ?? '')
  if (!f) return 'unknown'
  if (/havale|\beft\b|online|pesin/.test(f)) return 'prepaid'
  if (/kart|kredi/.test(f)) return 'card'
  if (/nakit/.test(f)) return 'cash'
  return 'unknown'
}

// ─────────────────────────────── Takip motoru ───────────────────────────────

export interface FollowupSettings {
  /** Mesai (İstanbul saati, ondalık). Görevler yalnız mesai içinde üretilir. */
  workStartHour: number
  workEndHour: number
  /** Sorunlu gönderi: günde / toplam en fazla temas. */
  problemMaxPerDay: number
  problemMaxTotal: number
  /** Ulaşılamayan sorunlu gönderiyi kaç saatte bir tekrar dene. */
  problemRetryHours: number
  /** Söz takibi ve erken müdahale: günde / toplam en fazla temas. */
  maxPerDay: number
  maxTotal: number
  /** Haber formundan sonra şubede bekleme süresi (gün) — kanal bazında; iade geri sayımı. */
  waitDaysByChannel: Record<string, number>
  defaultWaitDays: number
  /** Erken müdahale: kabulden bu kadar gün sonra hâlâ açık gönderi aranır ("48 saat kuralı"). */
  earlyAfterDays: number
  /** Erken müdahale kabulden en fazla bu kadar gün geriye bakar. */
  earlyLookbackDays: number
}

export const DEFAULT_FOLLOWUP_SETTINGS: FollowupSettings = {
  workStartHour: 9,
  workEndHour: 20,
  problemMaxPerDay: 5,
  problemMaxTotal: 20,
  problemRetryHours: 2,
  maxPerDay: 3,
  maxTotal: 12,
  // Aras'ta haber formu → iade medyanı ~3 gün (panel ölçümü); PTT ~7.
  waitDaysByChannel: { ARAS: 3, PTT: 7 },
  defaultWaitDays: 3,
  earlyAfterDays: 2,
  earlyLookbackDays: 10,
}

/** Söz verilen güne göre faz. */
export type PromisePhase = 'onceki_gun' | 'soz_gunu' | 'gecikti_1' | 'gecikti_2' | 'gecikti_3'

/** Faz → gün içi temas saatleri (İstanbul, ondalık). */
export const PROMISE_PHASE_SLOTS: Record<PromisePhase, number[]> = {
  onceki_gun: [17.5],
  soz_gunu: [10, 17],
  gecikti_1: [10, 13.5, 17.5],
  gecikti_2: [10, 13.5, 17.5],
  gecikti_3: [10],
}

export const PROMISE_PHASE_LABELS: Record<PromisePhase, string> = {
  onceki_gun: 'Yarın teslim alacak: hatırlat',
  soz_gunu: 'Bugün teslim alacaktı',
  gecikti_1: 'Söz günü geçti (1. gün)',
  gecikti_2: 'Söz günü geçti (2. gün)',
  gecikti_3: 'Son uyarı (3. gün)',
}

/** Faz → varsayılan müşteri mesaj şablonu anahtarı. */
export const PROMISE_PHASE_TEMPLATE: Record<PromisePhase, string> = {
  onceki_gun: 'hatirlatma',
  soz_gunu: 'soz_gunu',
  gecikti_1: 'soz_gecti',
  gecikti_2: 'son_uyari',
  gecikti_3: 'son_uyari',
}

/** diffDays = söz günü − bugün. */
export function promisePhaseOf(diffDays: number): PromisePhase | null {
  if (diffDays === 1) return 'onceki_gun'
  if (diffDays === 0) return 'soz_gunu'
  if (diffDays === -1) return 'gecikti_1'
  if (diffDays === -2) return 'gecikti_2'
  if (diffDays === -3) return 'gecikti_3'
  return null
}

/** İstanbul takvim günü (YYYY-MM-DD) ve ondalık saat. */
export function istanbulClock(now: Date = new Date()): { date: string; hour: number } {
  const p = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(now)
  const g = (t: string) => p.find((x) => x.type === t)?.value ?? '0'
  return { date: `${g('year')}-${g('month')}-${g('day')}`, hour: (Number(g('hour')) % 24) + Number(g('minute')) / 60 }
}

/** İki takvim günü (YYYY-MM-DD) arasındaki fark: a − b. */
export function diffCalendarDays(a: string, b: string): number {
  return Math.round((Date.parse(`${a}T12:00:00Z`) - Date.parse(`${b}T12:00:00Z`)) / DAY_MS)
}

/** İstanbul gününde verilen saat (ondalık) → Date. */
function atIstanbul(date: string, hour: number): Date {
  return new Date(Date.parse(`${date}T00:00:00+03:00`) + hour * 3_600_000)
}

/** Haber formundan sonra iadeye kalan gün (negatif: süre doldu). İlk haber formu yoksa null. */
export function daysToReturn(
  channel: string | null | undefined,
  firstHaberAt: string | null | undefined,
  now: Date = new Date(),
  settings: FollowupSettings = DEFAULT_FOLLOWUP_SETTINGS,
): number | null {
  if (!firstHaberAt) return null
  const wait = (channel && settings.waitDaysByChannel[channel]) || settings.defaultWaitDays
  return Math.round((wait - (now.getTime() - new Date(firstHaberAt).getTime()) / DAY_MS) * 10) / 10
}

/** Müşterinin OPOP'ta seçtiği ödeme (Webpostman ödeme türü DEĞİL). */
export type PaymentIntent = 'card' | 'cash' | 'prepaid' | 'unknown'

/** Görev motoru girdisi: gönderi + temas istatistikleri (DB'den derlenir). */
export interface FollowupShipment {
  shipmentId: string
  durum: number | null
  statuNo: string | null
  channel: string | null
  amount: number
  paymentIntent: PaymentIntent
  /** Kabul tarihi (YYYY-MM-DD). */
  alimTarihi: string | null
  degisimAt: string | null
  firstHaberAt: string | null
  problemActive: boolean
  stage: ProblemStage | null
  reason: ProblemReason | null
  contactsToday: number
  contactsTotal: number
  lastContactAt: string | null
  lastOutcome: string | null
  callbackAt: string | null
  assignedTo: string | null
}

export interface FollowupPromise {
  shipmentId: string
  /** Müşterinin teslim almayı söz verdiği gün (YYYY-MM-DD). */
  promisedDate: string
}

export type FollowupTaskKind = 'soz' | 'sorunlu' | 'erken'

export interface FollowupTask {
  key: string
  kind: FollowupTaskKind
  /** 3 kritik, 2 yüksek, 1 normal. */
  level: 1 | 2 | 3
  shipmentId: string
  phase: PromisePhase | null
  title: string
  why: string
  /** Önerilen müşteri mesaj şablonu anahtarı. */
  templateKey: string | null
  dueAt: string
  overdueMin: number
  assignedTo: string | null
}

/** P(teslim | yaş günde hâlâ açık) tablosu — bkz. computeDeliveryHazard. */
export type DeliveryHazardTable = Partial<Record<PaymentIntent, (number | null)[]>>

export interface BuildTasksInput {
  shipments: FollowupShipment[]
  promises?: FollowupPromise[]
  hazard?: DeliveryHazardTable
  now?: Date
  settings?: FollowupSettings
}

export interface BuildTasksResult {
  now: string
  work: boolean
  tasks: FollowupTask[]
  counts: { total: number; critical: number; soz: number; sorunlu: number; erken: number }
}

/**
 * Görev kuyruğunu üretir: (1) söz takibi, (2) sorunlu gönderilerin ilk/tekrar araması, (3) erken müdahale
 * (yolda 2. gününü geçmiş, henüz sorunlu olmayan açık gönderi). Mesai dışında görev üretilmez.
 * Sıra: söz/sorunlu her zaman erken müdahalenin önünde; sonra seviye, sonra gecikme.
 */
export function buildFollowupTasks(input: BuildTasksInput): BuildTasksResult {
  const now = input.now ?? new Date()
  const settings = input.settings ?? DEFAULT_FOLLOWUP_SETTINGS
  const { date: today, hour } = istanbulClock(now)
  const work = hour >= settings.workStartHour && hour < settings.workEndHour
  const tasks: FollowupTask[] = []
  const minutesLate = (due: Date) => Math.max(0, Math.round((now.getTime() - due.getTime()) / 60_000))
  const byId = new Map(input.shipments.map((s) => [s.shipmentId, s]))
  const open = (s: FollowupShipment) => s.durum !== 1 && s.statuNo !== '10' && !isReturningShipment(s) && !isNotDepartedShipment(s)

  // 1) Söz takibi — her gönderi için en güncel söz.
  const latestPromise = new Map<string, string>()
  for (const p of input.promises ?? []) {
    const prev = latestPromise.get(p.shipmentId)
    if (!prev || p.promisedDate > prev) latestPromise.set(p.shipmentId, p.promisedDate)
  }
  const promised = new Set<string>()
  for (const [shipmentId, promisedDate] of latestPromise) {
    const s = byId.get(shipmentId)
    if (!s || !open(s)) continue
    const phase = promisePhaseOf(diffCalendarDays(promisedDate, today))
    if (!phase) continue
    promised.add(shipmentId)
    if (!work) continue
    const slots = PROMISE_PHASE_SLOTS[phase]
    const required = slots.filter((h) => h <= hour).length
    if (s.contactsToday >= required || s.contactsToday >= settings.maxPerDay) continue
    const due = atIstanbul(today, slots[Math.min(s.contactsToday, slots.length - 1)])
    const late = phase === 'gecikti_2' || phase === 'gecikti_3'
    const overdueMin = minutesLate(due)
    const level: 1 | 2 | 3 =
      late || overdueMin > 180 || (s.amount >= 1500 && phase !== 'onceki_gun') ? 3 : phase === 'gecikti_1' || overdueMin > 60 ? 2 : 1
    tasks.push({
      key: `soz:${shipmentId}`, kind: 'soz', level, shipmentId, phase, title: PROMISE_PHASE_LABELS[phase],
      why: `Söz günü ${promisedDate.slice(5)}. Bugün ${s.contactsToday}/${required} temas yapıldı; ${
        phase === 'gecikti_3' ? 'bu son uyarı, sonra iade/tekrar dağıtım kararı verin.' : 'teslim alıp almadığını sorun.'
      }`,
      templateKey: PROMISE_PHASE_TEMPLATE[phase], dueAt: due.toISOString(), overdueMin, assignedTo: s.assignedTo,
    })
  }

  // 2) Sorunlu gönderiler — ilk arama, geri arama, ulaşılamayanı tekrar arama, olumlu ama teslim olmayanı doğrulama.
  if (work) {
    for (const s of input.shipments) {
      if (!s.problemActive || promised.has(s.shipmentId)) continue
      // Müşteri teslim almayacağını söyledi → arama yok (iade kararı operatörde/kargo talebinde).
      if (s.lastOutcome && (FINAL_OUTCOMES as string[]).includes(s.lastOutcome)) continue
      if (s.contactsToday >= settings.problemMaxPerDay || s.contactsTotal >= settings.problemMaxTotal) continue
      const callbackMs = s.callbackAt ? Date.parse(s.callbackAt) : null
      const callbackDue = callbackMs != null && callbackMs <= now.getTime()
      if (callbackMs != null && !callbackDue) continue // planlı geri arama zamanı gelmedi
      const sinceLastH = s.lastContactAt ? (now.getTime() - Date.parse(s.lastContactAt)) / 3_600_000 : null
      let why: string | null = null
      let due: Date | null = null
      let level: 1 | 2 | 3 = 1
      if (callbackDue) {
        why = 'Planladığınız geri arama zamanı geldi.'
        due = new Date(callbackMs!)
        level = 2
      } else if (s.contactsTotal === 0) {
        why = 'Yeni sorunlu gönderi: henüz hiç aranmadı. Hemen ara.'
        due = new Date(Math.max(s.degisimAt ? Date.parse(s.degisimAt) : now.getTime(), atIstanbul(today, settings.workStartHour).getTime()))
        level = 2
      } else if (s.lastOutcome && (UNREACHED_OUTCOMES as string[]).includes(s.lastOutcome) && sinceLastH != null && sinceLastH >= settings.problemRetryHours) {
        why = `Son denemede ulaşılamadı (${Math.round(sinceLastH)} saat önce). ${s.contactsTotal}. deneme — bırakma, tekrar ara.`
        due = new Date(Date.parse(s.lastContactAt!) + settings.problemRetryHours * 3_600_000)
        level = s.contactsTotal >= 5 ? 3 : s.contactsTotal >= 2 ? 2 : 1
      } else if (s.lastOutcome && (POSITIVE_OUTCOMES as string[]).includes(s.lastOutcome) && sinceLastH != null && sinceLastH >= 12) {
        why = 'Olumlu görüşüldü ama gönderi hâlâ teslim olmadı; sonucu doğrula.'
        due = new Date(Date.parse(s.lastContactAt!) + 12 * 3_600_000)
        level = 2
      }
      const kalan = daysToReturn(s.channel, s.firstHaberAt, now, settings)
      if (!why && kalan != null && kalan <= 1 && s.contactsToday < 1) {
        why = 'İade süresi dolmak üzere.'
        due = atIstanbul(today, settings.workStartHour)
        level = 3
      }
      if (!why || !due) continue
      if (kalan != null) {
        why += ` İadeye ${kalan <= 0 ? 'süre doldu' : `~${kalan} gün kaldı`} (haber formu ${String(s.firstHaberAt).slice(0, 10)}).`
        if (kalan <= 1) level = 3
        else if (kalan <= 2 && level < 2) level = 2
      }
      const overdueMin = minutesLate(due)
      if (overdueMin > 180) level = 3
      tasks.push({
        key: `sorunlu:${s.shipmentId}`, kind: 'sorunlu', level, shipmentId: s.shipmentId, phase: null,
        title: s.contactsTotal === 0 ? 'İlk arama' : 'Tekrar arama', why, templateKey: s.reason ?? null,
        dueAt: due.toISOString(), overdueMin, assignedTo: s.assignedTo,
      })
    }
  }

  // 3) Erken müdahale — yolda N. gününü geçmiş, henüz sorunlu olmayan açık gönderi (günde 1 temas).
  if (work) {
    for (const s of input.shipments) {
      if (s.problemActive || promised.has(s.shipmentId) || !open(s) || !s.alimTarihi) continue
      if (s.lastOutcome && (FINAL_OUTCOMES as string[]).includes(s.lastOutcome)) continue
      const age = diffCalendarDays(today, s.alimTarihi)
      if (age < settings.earlyAfterDays || age > settings.earlyLookbackDays) continue
      if (s.contactsToday >= 1 || s.contactsTotal >= settings.maxTotal) continue
      if (s.lastContactAt && now.getTime() - Date.parse(s.lastContactAt) < 24 * 3_600_000) continue
      const p = hazardAt(input.hazard, s.paymentIntent, age)
      const due = atIstanbul(today, 10)
      // Erken müdahale kritik seviyeye çıkmaz: asıl odak sorunluyu teslime çevirmek.
      const level: 1 | 2 = (p != null && p < 0.5) || s.channel === 'PTT' ? 2 : 1
      tasks.push({
        key: `erken:${s.shipmentId}`, kind: 'erken', level, shipmentId: s.shipmentId, phase: null,
        title: `Erken müdahale: yolda ${age}. gün`,
        why: `Yolda ${age}. gün ve hâlâ teslim olmadı${p != null ? `; bu noktada teslim olasılığı ~%${Math.round(p * 100)}` : ''}. ${
          s.paymentIntent === 'card'
            ? 'Adreste olmasını ve kartının/limitinin hazır olduğunu teyit edin.'
            : 'Adreste olmasını ve ödemeyi hazır bulundurmasını teyit edin.'
        }`,
        templateKey: s.paymentIntent === 'card' ? 'erken_kart' : 'erken_nakit', dueAt: due.toISOString(),
        overdueMin: minutesLate(due), assignedTo: s.assignedTo,
      })
    }
  }

  const tier = (k: FollowupTaskKind) => (k === 'erken' ? 0 : 1)
  tasks.sort((a, b) => tier(b.kind) - tier(a.kind) || b.level - a.level || b.overdueMin - a.overdueMin)
  return {
    now: now.toISOString(),
    work,
    tasks,
    counts: {
      total: tasks.length,
      critical: tasks.filter((t) => t.level === 3).length,
      soz: tasks.filter((t) => t.kind === 'soz').length,
      sorunlu: tasks.filter((t) => t.kind === 'sorunlu').length,
      erken: tasks.filter((t) => t.kind === 'erken').length,
    },
  }
}

// ─────────────────────────────── Teslim olasılığı (hazard) ───────────────────────────────

/** Sonuçlanmış gönderi örneği (olgun kohorttan). */
export interface HazardSample {
  intent: PaymentIntent
  /** Kabulden sonuca (teslim/iade) geçen gün. */
  resolveDays: number
  delivered: boolean
  /** Bu örnekten kaç adet (toplu sayımlar için; varsayılan 1). */
  count?: number
}

/**
 * P(sonunda teslim | kabulden `a` gün sonra hâlâ açık) tablosu, ödeme niyetine göre. Gün `a`'da açık =
 * sonuç günü > a. Örnek sayısı `minSamples`'tan azsa hücre null (yanıltıcı oran üretmesin).
 * Örnekler olgun kohorttan (kabulü ≥16 gün önce) alınmalı ki yolda kalanlar oranı çarpıtmasın.
 */
export function computeDeliveryHazard(
  samples: HazardSample[],
  opts: { maxAge?: number; minSamples?: number } = {},
): DeliveryHazardTable {
  const maxAge = opts.maxAge ?? 25
  const minSamples = opts.minSamples ?? 15
  const table: DeliveryHazardTable = {}
  const groups = new Map<PaymentIntent, HazardSample[]>()
  for (const s of samples) {
    const arr = groups.get(s.intent) ?? []
    arr.push(s)
    groups.set(s.intent, arr)
  }
  for (const [intent, arr] of groups) {
    const row: (number | null)[] = []
    const weight = (s: HazardSample) => s.count ?? 1
    for (let a = 0; a <= maxAge; a++) {
      const openAtA = arr.filter((s) => s.resolveDays > a)
      const n = openAtA.reduce((t, s) => t + weight(s), 0)
      const delivered = openAtA.filter((s) => s.delivered).reduce((t, s) => t + weight(s), 0)
      row[a] = n >= minSamples ? delivered / n : null
    }
    table[intent] = row
  }
  return table
}

/** Tablodan olasılık okur; o gün için veri yoksa en yakın önceki günün değerine düşer, hiç yoksa null. */
export function hazardAt(table: DeliveryHazardTable | undefined, intent: PaymentIntent, age: number): number | null {
  const row = table?.[intent]
  if (!row) return null
  for (let a = Math.min(Math.max(age, 0), row.length - 1); a >= 0; a--) {
    const v = row[a]
    if (v != null) return v
  }
  return null
}
